import React, { useState, useEffect } from 'react';
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Save,
  ArrowLeft
} from 'lucide-react';
import styles from './createNewClose.module.css';

// Template data with default sections and tasks
const defaultTemplate = {
  sections: [
    {
      id: 1,
      name: "Pre-Close",
      description: "Preparation tasks before the main close process",
      required: true,
      defaultTasks: [
        {
          name: "Request bank statements",
          description: "Contact client for all statements"
        },
        {
          name: "Bank reconciliation setup",
          description: "Prepare reconciliation files"
        },
        {
          name: "Review prior month notes",
          description: "Check for any carryover items"
        }
      ]
    },
    {
      id: 2,
      name: "Bookkeeping",
      description: "Recording all financial transactions",
      required: true,
      defaultTasks: [
        {
          name: "Process banking transactions",
          description: "Enter all bank transactions"
        },
        {
          name: "Record credit card activity",
          description: "Enter all credit card transactions"
        },
        {
          name: "Process AR/AP items",
          description: "Update accounts receivable and payable"
        },
        {
          name: "Reconcile accounts",
          description: "Ensure all accounts balance"
        }
      ]
    },
    {
      id: 3,
      name: "Review",
      description: "Checking the accuracy of financial records",
      required: true,
      defaultTasks: [
        {
          name: "Review balance sheet",
          description: "Check all balance sheet accounts"
        },
        {
          name: "Review income statement",
          description: "Analyze income statement for inconsistencies"
        },
        {
          name: "Verify tax accruals",
          description: "Ensure tax accruals are accurate"
        }
      ]
    },
    {
      id: 4,
      name: "Adjustments",
      description: "Making necessary corrections to financial records",
      required: false,
      defaultTasks: [
        {
          name: "Record adjusting entries",
          description: "Make any necessary adjustments"
        },
        {
          name: "Update depreciation",
          description: "Calculate and record depreciation"
        }
      ]
    }
  ],
  users: [
    { id: 1, name: "Sarah Wilson" },
    { id: 2, name: "David Lopez" },
    { id: 3, name: "Michelle Taylor" }
  ]
};

export function CreateNewClose({ onBack }) {
  // State for the form
  const [formData, setFormData] = useState({
    period: new Date().getMonth() + 1, // Default to current month (1-12)
    year: new Date().getFullYear(),
    dueDate: "",
    assignedUserId: "",
    status: "Not Started",
    sections: []
  });
  
  // State to track which sections are expanded
  const [expandedSections, setExpandedSections] = useState({});
  
  // State to track which sections are selected from the template
  const [selectedSections, setSelectedSections] = useState({});
  
  // Initialize on component mount
  useEffect(() => {
    // Set initial expanded state for all sections
    const expanded = {};
    defaultTemplate.sections.forEach(section => {
      expanded[section.id] = true;
    });
    setExpandedSections(expanded);
    
    // Set initial selected sections based on required status
    const selected = {};
    defaultTemplate.sections.forEach(section => {
      selected[section.id] = section.required;
    });
    setSelectedSections(selected);
    
    // Create the initial sections array from selected template sections
    const selectedSectionsList = defaultTemplate.sections
      .filter(section => section.required)
      .map(section => ({
        ...section,
        tasks: [...section.defaultTasks]
      }));
    
    setFormData(prev => ({
      ...prev,
      sections: selectedSectionsList
    }));
  }, []);
  
  // Toggle section expanded state
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };
  
  // Toggle section selection
  const toggleSectionSelection = (sectionId) => {
    // Can't deselect required sections
    const section = defaultTemplate.sections.find(s => s.id === sectionId);
    if (section.required && selectedSections[sectionId]) return;
    
    setSelectedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
    
    // Update formData sections based on selection
    if (!selectedSections[sectionId]) {
      // Adding section
      const sectionToAdd = defaultTemplate.sections.find(s => s.id === sectionId);
      setFormData(prev => ({
        ...prev,
        sections: [...prev.sections, {
          ...sectionToAdd,
          tasks: [...sectionToAdd.defaultTasks]
        }]
      }));
    } else {
      // Removing section
      setFormData(prev => ({
        ...prev,
        sections: prev.sections.filter(s => s.id !== sectionId)
      }));
    }
  };
  
  // Add a new task to a section
  const addTask = (sectionId) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id !== sectionId) return section;
        
        return {
          ...section,
          tasks: [
            ...section.tasks,
            {
              name: "",
              description: ""
            }
          ]
        };
      })
    }));
  };
  
  // Remove a task from a section
  const removeTask = (sectionId, taskIndex) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id !== sectionId) return section;
        
        return {
          ...section,
          tasks: section.tasks.filter((_, index) => index !== taskIndex)
        };
      })
    }));
  };
  
  // Update task data
  const updateTask = (sectionId, taskIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id !== sectionId) return section;
        
        return {
          ...section,
          tasks: section.tasks.map((task, index) => {
            if (index !== taskIndex) return task;
            
            return {
              ...task,
              [field]: value
            };
          })
        };
      })
    }));
  };
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Generate periods array for dropdown (months, quarters, blank)
  const periods = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
    { value: 'Q1', label: "Q1" },
    { value: 'Q2', label: "Q2" },
    { value: 'Q3', label: "Q3" },
    { value: 'Q4', label: "Q4" }
  ];
  
  // Generate years array for dropdown (current year +/- 2 years)
  const currentYear = new Date().getFullYear();
  const years = [
    currentYear - 2,
    currentYear - 1,
    currentYear,
    currentYear + 1,
    currentYear + 2
  ];
  
  // Calculate a suggested due date (15th of next period or next month if period is month)
  const calculateSuggestedDueDate = () => {
    let period = formData.period;
    let year = parseInt(formData.year);
    if (period === '' || period === 'Q1' || period === 'Q2' || period === 'Q3' || period === 'Q4') {
      // For blank or quarter, just use 15th of next month
      let nextMonth = new Date().getMonth() + 2;
      let nextYear = year;
      if (nextMonth > 12) {
        nextMonth = 1;
        nextYear += 1;
      }
      return `${nextYear}-${nextMonth.toString().padStart(2, '0')}-15`;
    } else {
      // For month, suggest 15th of next month
      let nextMonth = parseInt(period) + 1;
      let nextYear = year;
      if (nextMonth > 12) {
        nextMonth = 1;
        nextYear += 1;
      }
      return `${nextYear}-${nextMonth.toString().padStart(2, '0')}-15`;
    }
  };
  
  // Handler to set the suggested due date
  const setSuggestedDueDate = () => {
    setFormData(prev => ({
      ...prev,
      dueDate: calculateSuggestedDueDate()
    }));
  };
  
  // Calculate if the form is valid
  const isFormValid = () => {
    return (
      formData.dueDate !== "" &&
      formData.assignedUserId !== "" &&
      formData.period !== '' &&
      formData.sections.length > 0 &&
      formData.sections.every(section => 
        section.tasks.length > 0 &&
        section.tasks.every(task => 
          task.name.trim() !== "" && 
          task.description.trim() !== ""
        )
      )
    );
  };
  
  // Handle form submission
  const handleSubmit = () => {
    if (!isFormValid()) {
      alert("Please complete all required fields.");
      return;
    }
    
    // In a real app, you would send this data to your API
    console.log("Submitting form data:", formData);
    
    // Simulate success
    alert("Month-end close created successfully!");
  };
  
  return (
    <div className={styles.appContainer}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerRow}>
            <div className={styles.headerLeft}>
              <button className={styles.backButton} onClick={onBack}>
                <ArrowLeft size={20} />
              </button>
              <h2 className={styles.pageTitle}>Create New Month-End Close</h2>
            </div>
            <div>
              <button
                className={
                  isFormValid()
                    ? styles.saveButton
                    : `${styles.saveButton} ${styles.saveButtonDisabled}`
                }
                onClick={handleSubmit}
                disabled={!isFormValid()}
              >
                <Save size={16} />
                Save & Create
              </button>
            </div>
          </div>
        </div>
      </header>
      {/* Main content */}
      <main className={styles.mainContent}>
        <div className={styles.contentContainer}>
          {/* Basic Information */}
          <div className={styles.sectionCard}>
            <h3 className={styles.sectionTitle}>Basic Information</h3>
            <div className={styles.formGrid}>
              {/* Remove Client selection */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Assigned To</label>
                <select
                  name="assignedUserId"
                  value={formData.assignedUserId}
                  onChange={handleChange}
                  className={styles.formSelect}
                >
                  <option value="">Select a user</option>
                  {defaultTemplate.users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Period/Year</label>
                <div className={styles.dateInputs}>
                  <select
                    name="period"
                    value={formData.period}
                    onChange={handleChange}
                    className={styles.formSelect}
                  >
                    {periods.map(period => (
                      <option key={period.value} value={period.value}>
                        {period.label}
                      </option>
                    ))}
                  </select>
                  <select
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    className={styles.formSelect}
                  >
                    {years.map(year => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Due Date</label>
                <div className={styles.dateInputs}>
                  <input
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleChange}
                    className={styles.formInput}
                  />
                  <button
                    type="button"
                    onClick={setSuggestedDueDate}
                    className={styles.suggestButton}
                  >
                    Suggest Date
                  </button>
                </div>
                <p className={styles.helpText}>
                  Typically the 15th of the following month
                </p>
              </div>
            </div>
          </div>
          {/* Sections Selection */}
          <div className={styles.sectionCard}>
            <h3 className={styles.sectionTitle}>Sections</h3>
            <p className={styles.sectionDescription}>
              Select the sections to include in this month-end close. Required sections cannot be removed.
            </p>
            <div className={styles.sectionsGrid}>
              {defaultTemplate.sections.map(section => (
                <div
                  key={section.id}
                  className={
                    selectedSections[section.id]
                      ? `${styles.sectionItem} ${styles.sectionItemSelected}`
                      : styles.sectionItem
                  }
                >
                  <div className={styles.sectionHeader}>
                    <div className={styles.sectionCheckboxContainer}>
                      <input
                        type="checkbox"
                        id={`section-${section.id}`}
                        checked={selectedSections[section.id]}
                        onChange={() => toggleSectionSelection(section.id)}
                        disabled={section.required}
                        className={styles.sectionCheckbox}
                      />
                      <label htmlFor={`section-${section.id}`} className={styles.sectionLabel}>
                        {section.name}
                        {section.required && (
                          <span className={styles.requiredBadge}>Required</span>
                        )}
                      </label>
                    </div>
                    <div className={styles.taskCount}>
                      {section.defaultTasks.length} default tasks
                    </div>
                  </div>
                  <p className={styles.sectionDescriptionText}>{section.description}</p>
                </div>
              ))}
            </div>
          </div>
          {/* Section Configuration */}
          {formData.sections.length > 0 && (
            <div className={styles.sectionCard}>
              <h3 className={styles.sectionTitle}>Section Configuration</h3>
              <p className={styles.sectionDescription}>
                Customize the tasks for each section. You can add, edit, or remove tasks as needed.
              </p>
              <div className={styles.sectionList}>
                {formData.sections.map(section => (
                  <div key={section.id} className={styles.configSection}>
                    {/* Section header */}
                    <div
                      className={styles.configSectionHeader}
                      onClick={() => toggleSection(section.id)}
                    >
                      <h4 className={styles.configSectionTitle}>{section.name}</h4>
                      <button type="button" className={styles.toggleButton}>
                        {expandedSections[section.id] ? (
                          <ChevronDown size={20} />
                        ) : (
                          <ChevronUp size={20} />
                        )}
                      </button>
                    </div>
                    {/* Tasks */}
                    {expandedSections[section.id] && (
                      <div className={styles.tasksContainer}>
                        <div className={styles.tasksList}>
                          {section.tasks.map((task, taskIndex) => (
                            <div key={taskIndex} className={styles.taskItem}>
                              <div className={styles.taskHeader}>
                                <h5 className={styles.taskTitle}>Task {taskIndex + 1}</h5>
                                <button
                                  type="button"
                                  onClick={() => removeTask(section.id, taskIndex)}
                                  className={styles.deleteButton}
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                              <div className={styles.taskForm}>
                                <div className={styles.taskFormGroup}>
                                  <label className={styles.formLabel}>Task Name</label>
                                  <input
                                    type="text"
                                    value={task.name}
                                    onChange={e => updateTask(section.id, taskIndex, 'name', e.target.value)}
                                    className={styles.formInput}
                                    placeholder="Enter task name"
                                  />
                                </div>
                                <div className={styles.taskFormGroup}>
                                  <label className={styles.formLabel}>Task Description</label>
                                  <input
                                    type="text"
                                    value={task.description}
                                    onChange={e => updateTask(section.id, taskIndex, 'description', e.target.value)}
                                    className={styles.formInput}
                                    placeholder="Enter task description"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => addTask(section.id)}
                          className={styles.addTaskButton}
                        >
                          <Plus size={16} className={styles.iconPlus} />
                          Add Task
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Submit Button - Mobile */}
          <div className={styles.mobileSubmit}>
            <button
              type="button"
              onClick={handleSubmit}
              className={
                isFormValid()
                  ? styles.mobileSaveButton
                  : `${styles.mobileSaveButton} ${styles.mobileSaveButtonDisabled}`
              }
              disabled={!isFormValid()}
            >
              <Save size={16} />
              Save & Create
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};
